import {
  CameraRequestError,
  TalkError,
  type ArchiveSegment,
  type CameraRecording,
  type CameraRepository,
  type VideoFeature,
} from "../domain";
import { cameraKindsOf } from "./createCameraHttpRepository";

/** The mock's stream URLs: the player draws a test card instead of
 *  connecting anywhere (player/liveVideo). */
export const MOCK_STREAM_PREFIX = "mock-camera:";
/** The mock's talk URLs: answered in the page, never sent (player/talk). */
export const MOCK_TALK_PREFIX = "mock-talk:";

/** The mock's footage: 20-minute recordings on channels 1 and 2 (none on 3
 *  and 4), every fourth one missing -- a gap to play through. */
const MOCK_RECORDING_MS = 20 * 60_000;
const MOCK_RECORDED_CHANNELS = [1, 2] as const;

export function mockRecordings(fromMs: number, toMs: number): CameraRecording[] {
  const recordings: CameraRecording[] = [];
  const first = Math.floor(fromMs / MOCK_RECORDING_MS);
  for (let block = first; block * MOCK_RECORDING_MS < toMs; block += 1) {
    if (block % 4 === 2) continue;
    const startMs = Math.max(fromMs, block * MOCK_RECORDING_MS);
    const endMs = Math.min(toMs, (block + 1) * MOCK_RECORDING_MS);
    for (const channel of MOCK_RECORDED_CHANNELS) {
      recordings.push({
        channel,
        startAt: new Date(startMs).toISOString(),
        endAt: new Date(endMs).toISOString(),
      });
    }
  }
  return recordings;
}

/** The mock's archive: a two-minute incident every five minutes (from
 *  1:30 into each) on channels 1 and 2 -- so most demo alerts have one --
 *  as twelve 10-second files (the real ones run up to 10 minutes, so the
 *  player also steps from one file to the next), each a quick 10-second
 *  sample MP4. */
const MOCK_INCIDENT_EVERY_MS = 5 * 60_000;
const MOCK_INCIDENT_AT_MS = 90_000;
const MOCK_INCIDENT_MS = 2 * 60_000;
const MOCK_FILE_MS = 10_000;
export const MOCK_ARCHIVE_URL =
  "https://test-videos.co.uk/vids/bigbuckbunny/mp4/h264/360/Big_Buck_Bunny_360_10s_1MB.mp4";

export function mockArchive(channel: number, fromMs: number, toMs: number): ArchiveSegment[] {
  if (!MOCK_RECORDED_CHANNELS.some((recorded) => recorded === channel)) return [];
  const segments: ArchiveSegment[] = [];
  const first = Math.floor((fromMs - MOCK_INCIDENT_AT_MS - MOCK_INCIDENT_MS) / MOCK_INCIDENT_EVERY_MS);
  for (let block = first; block * MOCK_INCIDENT_EVERY_MS < toMs; block += 1) {
    const incidentMs = block * MOCK_INCIDENT_EVERY_MS + MOCK_INCIDENT_AT_MS;
    for (let startMs = incidentMs; startMs < incidentMs + MOCK_INCIDENT_MS; startMs += MOCK_FILE_MS) {
      const endMs = startMs + MOCK_FILE_MS;
      if (endMs <= fromMs || startMs >= toMs) continue;
      segments.push({
        id: `mock-archive-${String(channel)}-${String(startMs)}`,
        deviceId: "mock-camera",
        channel,
        startMs,
        endMs,
        url: MOCK_ARCHIVE_URL,
        urlExpiresMs: Date.now() + 15 * 60_000,
      });
    }
  }
  return segments;
}

const BSJ_FEATURES: readonly VideoFeature[] = ["LIVE", "RECORDINGS", "PLAYBACK", "CLIPS", "SNAPSHOTS"];

export interface MockCameraOptions {
  /** Camera devices with no live cameras (a recorder, say). */
  readonly withoutCameras?: readonly string[];
  /** Camera devices that are a JIMI JC400 (cabin and road cameras, live
   *  only, no intercom); the rest are a four-camera BSJ dashcam (JT/T 1078,
   *  which can talk). */
  readonly jc400?: readonly string[];
  readonly startDelayMs?: number;
}

/**
 * Dashcams on the tracking mock, by camera device id (the mock vehicles'
 * `cameraDeviceId`), the same models as the real look-up sees. Starting
 * takes a moment, like a real camera answering.
 */
export function createMockCameraRepository(options: MockCameraOptions = {}): CameraRepository {
  const delay = options.startDelayMs ?? 1200;
  let session = 0;
  const camerasOf = (deviceId: string) => {
    if (options.withoutCameras?.includes(deviceId)) {
      return { cameras: [], features: [], canTalk: false };
    }
    return options.jc400?.includes(deviceId)
      ? { cameras: cameraKindsOf("JC400P", 2), features: ["LIVE"] as VideoFeature[], canTalk: false }
      : { cameras: cameraKindsOf("BSJ", 4), features: [...BSJ_FEATURES], canTalk: true };
  };
  const later = <T>(value: () => T) =>
    new Promise<T>((resolve) => setTimeout(() => resolve(value()), delay));
  return {
    getCameras(deviceId) {
      return Promise.resolve({ deviceId, ...camerasOf(deviceId) });
    },
    startLive(deviceId, channel) {
      if (!camerasOf(deviceId).cameras.some((camera) => camera.channel === channel)) {
        return Promise.reject(new CameraRequestError("NOT_FOUND"));
      }
      session += 1;
      const url = `${MOCK_STREAM_PREFIX}${deviceId}/${String(channel)}`;
      const sessionId = `mock-session-${String(session)}`;
      return later(() => ({
        sessionId,
        channel,
        hlsUrl: url,
        webrtcUrl: url,
        endsAt: new Date(Date.now() + 10 * 60_000).toISOString(),
      }));
    },
    stopLive() {
      return Promise.resolve();
    },
    startIntercom(deviceId, channel) {
      if (!camerasOf(deviceId).canTalk) return Promise.reject(new TalkError("NOT_SUPPORTED"));
      session += 1;
      const sessionId = `mock-talk-${String(session)}`;
      return later(() => ({
        sessionId,
        channel,
        talkUrl: `${MOCK_TALK_PREFIX}${sessionId}`,
        endsAt: new Date(Date.now() + 10 * 60_000).toISOString(),
      }));
    },
    stopIntercom() {
      return Promise.resolve();
    },
    listRecordings(_deviceId, range) {
      return later(() => mockRecordings(Date.parse(range.from), Date.parse(range.to)));
    },
    startRecordedVideo(deviceId, channel, range) {
      session += 1;
      const url = `${MOCK_STREAM_PREFIX}${deviceId}/${String(channel)} from ${range.from.slice(11, 19)}`;
      const sessionId = `mock-playback-${String(session)}`;
      return later(() => ({
        sessionId,
        channel,
        hlsUrl: url,
        webrtcUrl: url,
        endsAt: new Date(Date.now() + 60 * 60_000).toISOString(),
      }));
    },
    controlRecordedVideo() {
      return Promise.resolve();
    },
    stopRecordedVideo() {
      return Promise.resolve();
    },
    getArchive(_assetId, channel, range) {
      const fromMs = Date.parse(range.from);
      const toMs = Date.parse(range.to);
      return later(() => ({ channel, fromMs, toMs, segments: mockArchive(channel, fromMs, toMs) }));
    },
    getArchivedSeconds(_assetId, channel, range) {
      const fromMs = Date.parse(range.from);
      const toMs = Date.parse(range.to);
      const archivedMs = mockArchive(channel, fromMs, toMs).reduce(
        (total, segment) => total + Math.min(toMs, segment.endMs) - Math.max(fromMs, segment.startMs),
        0,
      );
      return Promise.resolve(Math.round(archivedMs / 1000));
    },
  };
}
